import { Injectable } from '@angular/core';
import {
  ApplicationEvent,
  Box,
  Customer,
  CustomerLightData,
  DEMO_CUSTOMER,
  DEMO_PARTNER,
  FindingDto,
  MailConfig,
  Partner,
  PartnerStatistics,
  PentestCalendarEvent,
  PentestScan,
  PentestStatistics,
  User,
  UserAccount,
  BoxUpdateRequest,
  AdminBoxUpdateRequestDto,
} from '../models';
import {
  ApplicationEventType,
  LanguageEnum,
  OperatingSystem,
  PentestStatus,
  PentestType,
  Role,
  ScanModule,
  ScanType,
  Severity,
} from '../enums';
import { addMinutes, formatISO, subDays, subHours } from 'date-fns';
import { LastResultStatus, ScanConfig } from '../models/scan-config';
import { ReportTypeEnum } from '../enums/report-type.enum';
import { GetPentestConfigData } from '../models/get-pentest-config.data';
import { DEMO_PARTNER_LIGHT, PartnerLightData } from '../models/partner-light-data';
import { IdentecoConfigBase } from '../models/identeco-config-base';
import { environment } from '../../../environment/environment';
import { DarknetScanEmailAddress } from '../models/darknet-scan-email-address';
import { BreachLenseConfigBase } from '../models/breach-lense-config-base';
import { BreachLenseFindingDto } from '../models/breach-lense-finding-dto';
import { ScanResultType } from '../enums/scan-result-type.enum';

/**
 * AI generated.
 */
@Injectable({ providedIn: 'root' })
export class DemoDataService {
  private demoPentestStatistics: PentestStatistics | null = null;
  private demoPentestScans: PentestScan[] | null = null;
  private demoPentestFindings: Record<string, FindingDto[]> = {};
  private demoBreachLenseFindings: BreachLenseFindingDto[] | null = null;
  private demoBoxes: Box[] | null = null;
  private demoUsers: User[] | null = null;
  private demoIdentecoConfigs: IdentecoConfigBase[] | null = null;
  private demoBreachLenseConfigs: BreachLenseConfigBase[] | null = null;
  private demoDarknetScanEmailAddresses: DarknetScanEmailAddress[] | null = null;
  private demoScanConfigurations: ScanConfig[] | null = null;
  private demoPentestCalendarEvents: PentestCalendarEvent[] | null = null;
  private demoPartnerPentestCalendarEvents: PentestCalendarEvent[] | null = null;

  private readonly customerWeeklyStats: Record<
    number,
    {
      week: number;
      scans: number;
      critical: number;
      high: number;
      medium: number;
      low: number;
    }[]
  > = {
    0: [
      { week: 3, scans: 2, critical: 1, high: 3, medium: 2, low: 10 },
      { week: 2, scans: 2, critical: 0, high: 0, medium: 2, low: 4 },
      { week: 1, scans: 2, critical: 1, high: 3, medium: 5, low: 10 },
      { week: 0, scans: 2, critical: 0, high: 1, medium: 2, low: 5 },
    ],
    1: [
      { week: 3, scans: 2, critical: 2, high: 5, medium: 3, low: 15 },
      { week: 2, scans: 2, critical: 0, high: 1, medium: 3, low: 6 },
      { week: 1, scans: 2, critical: 2, high: 5, medium: 8, low: 15 },
      { week: 0, scans: 2, critical: 1, high: 2, medium: 4, low: 8 },
    ],
    2: [
      { week: 3, scans: 2, critical: 1, high: 2, medium: 1, low: 8 },
      { week: 2, scans: 2, critical: 0, high: 0, medium: 2, low: 3 },
      { week: 1, scans: 2, critical: 1, high: 2, medium: 4, low: 8 },
      { week: 0, scans: 2, critical: 0, high: 1, medium: 2, low: 4 },
    ],
    3: [
      { week: 3, scans: 2, critical: 1, high: 2, medium: 1, low: 6 },
      { week: 2, scans: 2, critical: 0, high: 1, medium: 2, low: 3 },
      { week: 1, scans: 2, critical: 1, high: 6, medium: 7, low: 14 },
      { week: 0, scans: 2, critical: 3, high: 5, medium: 6, low: 12 },
    ],
    4: [
      { week: 3, scans: 2, critical: 1, high: 3, medium: 2, low: 10 },
      { week: 2, scans: 2, critical: 0, high: 1, medium: 2, low: 4 },
      { week: 1, scans: 2, critical: 1, high: 3, medium: 5, low: 10 },
      { week: 0, scans: 2, critical: 0, high: 1, medium: 3, low: 6 },
    ],
    5: [
      { week: 3, scans: 2, critical: 0, high: 1, medium: 1, low: 4 },
      { week: 2, scans: 2, critical: 0, high: 0, medium: 1, low: 3 },
      { week: 1, scans: 2, critical: 2, high: 3, medium: 6, low: 12 },
      { week: 0, scans: 2, critical: 3, high: 5, medium: 5, low: 10 },
    ],
    6: [
      { week: 3, scans: 2, critical: 3, high: 7, medium: 4, low: 25 },
      { week: 2, scans: 2, critical: 1, high: 2, medium: 5, low: 5 },
      { week: 1, scans: 2, critical: 3, high: 7, medium: 12, low: 25 },
      { week: 0, scans: 2, critical: 1, high: 3, medium: 6, low: 12 },
    ],
    7: [
      { week: 3, scans: 2, critical: 1, high: 1, medium: 1, low: 4 },
      { week: 2, scans: 2, critical: 0, high: 0, medium: 1, low: 2 },
      { week: 1, scans: 2, critical: 2, high: 1, medium: 5, low: 10 },
      { week: 0, scans: 2, critical: 2, high: 5, medium: 4, low: 8 },
    ],
    8: [
      { week: 3, scans: 2, critical: 2, high: 4, medium: 2, low: 12 },
      { week: 2, scans: 2, critical: 0, high: 1, medium: 2, low: 4 },
      { week: 1, scans: 2, critical: 2, high: 4, medium: 6, low: 12 },
      { week: 0, scans: 2, critical: 2, high: 3, medium: 5, low: 10 },
    ],
    9: [
      { week: 3, scans: 2, critical: 1, high: 2, medium: 1, low: 8 },
      { week: 2, scans: 2, critical: 0, high: 0, medium: 1, low: 3 },
      { week: 1, scans: 2, critical: 1, high: 2, medium: 4, low: 8 },
      { week: 0, scans: 2, critical: 1, high: 1, medium: 3, low: 6 },
    ],
  };

  private readonly FINDING_TEMPLATES: Record<string, { name: string; description: string }[]> = {
    [Severity.Critical]: [
      {
        name: 'Remote Code Execution via SMB',
        description: 'Vulnerability in SMBv1 allows for remote code execution on the target host 192.168.1.50:445.',
      },
      {
        name: 'Exposed Administrative Interface with Default Credentials',
        description:
          'The administrative interface at http://192.168.1.12:8080/admin is accessible with default credentials (admin/admin).',
      },
      {
        name: 'SQL Injection in Login Form',
        description: 'The login form at https://dmz-web.demo.com/login is vulnerable to blind SQL injection.',
      },
    ],
    [Severity.High]: [
      {
        name: 'Weak SSH Key Exchange Algorithms',
        description:
          'The SSH service at 192.168.1.75:22 supports weak key exchange algorithms like diffie-hellman-group1-sha1.',
      },
      {
        name: 'Outdated Web Server Version',
        description:
          'The Nginx server (v1.10.3) on 192.168.1.10:80 is outdated and contains multiple known vulnerabilities.',
      },
      {
        name: 'Unencrypted Transmission of Sensitive Information',
        description: 'Sensitive user data is transmitted over unencrypted HTTP on port 80.',
      },
      {
        name: 'Hardcoded API Key in JavaScript',
        description: 'An API key was found in a publicly accessible JavaScript file.',
      },
    ],
    [Severity.Medium]: [
      {
        name: 'SSL/TLS Certificate with Weak Signature Algorithm',
        description: 'The SSL certificate for https://extern-app.demo.com uses a weak signature algorithm (SHA-1).',
      },
      {
        name: 'Directory Listing Enabled',
        description: 'Directory listing is enabled on the web server at http://192.168.1.20/uploads/.',
      },
      {
        name: 'Missing Security Headers',
        description:
          'Several security headers (X-Frame-Options, Content-Security-Policy) are missing from the response.',
      },
      {
        name: 'Exposed Credentials in Configuration File',
        description:
          'A backup configuration file (.env.bak) containing database credentials was found in the web root.',
      },
    ],
    [Severity.Low]: [
      {
        name: 'ICMP Timestamp Response',
        description:
          'The remote host 192.168.1.100 responds to ICMP timestamp requests, allowing an attacker to determine the system time.',
      },
      {
        name: 'TCP Timestamp Response',
        description: 'The remote host 192.168.1.100 responds to TCP timestamp requests.',
      },
      {
        name: 'SSH Banner Disclosure',
        description: 'The SSH banner reveals the exact version of the SSH daemon.',
      },
      {
        name: 'Weak Password Policy for Guest Account',
        description: 'The guest account on 192.168.1.105 does not require a strong password.',
      },
    ],
  };

  getPentestStatistics(): PentestStatistics {
    if (this.demoPentestStatistics) {
      return this.demoPentestStatistics;
    }

    const scans = this.getPentestScans();
    const today = new Date();
    const generateStats = (type: 'impactsCount' | 'weaknessesCount' | 'credentialsCount') => {
      const statsMap: Record<string, number> = {};
      scans.forEach((scan) => {
        const date = formatISO(new Date(scan.completedAt), { representation: 'date' });
        statsMap[date] = (statsMap[date] || 0) + (scan[type] as number);
      });

      return Array.from({ length: 28 }, (_, i) => {
        const date = formatISO(subDays(today, 27 - i), { representation: 'date' });
        return {
          value: statsMap[date] || 0,
          date,
        };
      });
    };

    const impactsStats = Array.from({ length: 28 }, (_, i) => {
      const date = formatISO(subDays(today, 27 - i), { representation: 'date' });
      const dailyScans = scans.filter((s) => formatISO(new Date(s.completedAt), { representation: 'date' }) === date);
      const totalImpacts = dailyScans.reduce((acc, s) => acc + s.impactsCount, 0);
      return {
        value: totalImpacts,
        date,
      };
    });

    this.demoPentestStatistics = PentestStatistics.mapFromApiValue({
      internalPentestsCount: scans.filter((s) => s.type === PentestType.NodeZero).length,
      externalPentestsCount: scans.filter((s) => s.type === PentestType.ExternalAttack).length,
      uniqHostsCount: 45,
      hostsCountMax: Math.max(...scans.map((s) => s.hostsCount)),
      lastestImpacts: impactsStats,
      lastestWeaknesses: generateStats('weaknessesCount'),
      lastestCredentials: generateStats('credentialsCount'),
    });
    return this.demoPentestStatistics;
  }

  getPentestScans(ids?: string[] | null): PentestScan[] {
    if (this.demoPentestScans) {
      return ids ? this.demoPentestScans.filter((s) => ids.includes(s.id)) : this.demoPentestScans;
    }
    const today = new Date();
    const scanTemplates = [
      { name: 'LAN', baseDuration: 3 * 3600, type: PentestType.NodeZero, baseHostCount: 40 },
      { name: 'DMZ', baseDuration: 3 * 3600, type: PentestType.NodeZero, baseHostCount: 15 },
      { name: 'extern', baseDuration: 3600, type: PentestType.ExternalAttack, baseHostCount: 5 },
      { name: 'Lab', baseDuration: 4 * 3600, type: PentestType.NodeZero, baseHostCount: 25 },
    ];

    const scanDefinitions: { templateIdx: number; weekOffset: number; counts: Partial<Record<Severity, number>> }[] = [
      // Week 0
      {
        templateIdx: 0,
        weekOffset: 0,
        counts: { [Severity.Critical]: 0, [Severity.High]: 1, [Severity.Medium]: 2, [Severity.Low]: 5 },
      },
      {
        templateIdx: 1,
        weekOffset: 0,
        counts: { [Severity.Critical]: 0, [Severity.High]: 0, [Severity.Medium]: 0, [Severity.Low]: 1 },
      },
      {
        templateIdx: 2,
        weekOffset: 0,
        counts: { [Severity.Critical]: 0, [Severity.High]: 0, [Severity.Medium]: 1, [Severity.Low]: 2 },
      },
      {
        templateIdx: 3,
        weekOffset: 0,
        counts: { [Severity.Critical]: 1, [Severity.High]: 1, [Severity.Medium]: 1, [Severity.Low]: 0 },
      },
      // Week 1
      {
        templateIdx: 0,
        weekOffset: 1,
        counts: { [Severity.Critical]: 10, [Severity.High]: 10, [Severity.Medium]: 11, [Severity.Low]: 21 },
      },
      {
        templateIdx: 1,
        weekOffset: 1,
        counts: { [Severity.Critical]: 0, [Severity.High]: 0, [Severity.Medium]: 15, [Severity.Low]: 22 },
      },
      {
        templateIdx: 2,
        weekOffset: 1,
        counts: { [Severity.Critical]: 0, [Severity.High]: 0, [Severity.Medium]: 22, [Severity.Low]: 23 },
      },
      {
        templateIdx: 3,
        weekOffset: 1,
        counts: { [Severity.Critical]: 10, [Severity.High]: 10, [Severity.Medium]: 25, [Severity.Low]: 15 },
      },
      // Week 2
      {
        templateIdx: 0,
        weekOffset: 2,
        counts: { [Severity.Critical]: 3, [Severity.High]: 7, [Severity.Medium]: 25, [Severity.Low]: 22 },
      },
      {
        templateIdx: 1,
        weekOffset: 2,
        counts: { [Severity.Critical]: 0, [Severity.High]: 0, [Severity.Medium]: 5, [Severity.Low]: 23 },
      },
      {
        templateIdx: 2,
        weekOffset: 2,
        counts: { [Severity.Critical]: 0, [Severity.High]: 0, [Severity.Medium]: 5, [Severity.Low]: 24 },
      },
      {
        templateIdx: 3,
        weekOffset: 2,
        counts: { [Severity.Critical]: 6, [Severity.High]: 10, [Severity.Medium]: 16, [Severity.Low]: 0 },
      },
      // Week 3
      {
        templateIdx: 0,
        weekOffset: 3,
        counts: { [Severity.Critical]: 14, [Severity.High]: 28, [Severity.Medium]: 32, [Severity.Low]: 23 },
      },
      {
        templateIdx: 1,
        weekOffset: 3,
        counts: { [Severity.Critical]: 0, [Severity.High]: 0, [Severity.Medium]: 1, [Severity.Low]: 24 },
      },
      {
        templateIdx: 2,
        weekOffset: 3,
        counts: { [Severity.Critical]: 0, [Severity.High]: 0, [Severity.Medium]: 0, [Severity.Low]: 25 },
      },
      {
        templateIdx: 3,
        weekOffset: 3,
        counts: { [Severity.Critical]: 20, [Severity.High]: 25, [Severity.Medium]: 38, [Severity.Low]: 20 },
      },
    ];

    this.demoPentestScans = scanDefinitions.map((scanDefinition, i) => {
      const scanTemplate = scanTemplates[scanDefinition.templateIdx];
      const scanId = `demo-pentest-${i + 1}`;
      const startDate = subDays(today, scanDefinition.weekOffset * 7 + (i % 4));
      startDate.setUTCHours(2, 0, 0, 0);

      const durationS = scanTemplate.baseDuration + (i % 31) * 60 - 450;
      const completedDate = new Date(startDate.getTime() + durationS * 1000);

      const findings = this.generateFindings(scanDefinition, scanTemplate.type);
      this.demoPentestFindings[scanId] = findings;

      const maxSeverity = findings.length ? Math.max(...findings.map((f) => f.severity)) : Severity.Info;
      const hostVariance = Math.floor(scanTemplate.baseHostCount * 0.05);
      const hostCount = scanTemplate.baseHostCount + (i % (hostVariance * 2 + 1)) - hostVariance;

      return {
        id: scanId,
        name: scanTemplate.name,
        type: scanTemplate.type,
        status: PentestStatus.Done,
        createdAt: startDate.toISOString(),
        completedAt: completedDate.toISOString(),
        canceledAt: '',
        durationS,
        nodeZeroIp: `192.168.1.${100 + i}`,
        impactsCount: Math.round((findings.length / 10) * (1 + ((i % 5) * 0.1 - 0.2))),
        weaknessesCount: findings.length,
        credentialsCount: findings.filter((f) => f.name.toLowerCase().includes('credential')).length,
        hostsCount: hostCount,
        runner: this.getBoxes()[i % this.getBoxes().length],
        fromNessus: false,
        historyId: `hist-${i + 1}`,
        resultStatus: maxSeverity,
        scanResultType: ScanResultType.HORIZON,
      };
    });
    const darknetScanId = 'demo-darknet-scan-example-com';
    const darknetFindings = this.getBreachLenseFindings();
    const darknetScanDate = new Date();

    this.demoPentestFindings[darknetScanId] = darknetFindings;

    const darknetScan: PentestScan = {
      id: darknetScanId,
      name: "Darknet scan 'example.com'",
      type: PentestType.ExternalAttack,
      status: PentestStatus.Done,
      createdAt: darknetScanDate.toISOString(),
      completedAt: darknetScanDate.toISOString(),
      canceledAt: '',
      durationS: 1,
      nodeZeroIp: '',
      impactsCount: darknetFindings.length,
      weaknessesCount: darknetFindings.length,
      credentialsCount: darknetFindings.length,
      hostsCount: 0,
      runner: this.getBoxes()[0],
      fromNessus: false,
      historyId: 'demo-darknet-history-example-com',
      resultStatus: Severity.Critical,
      scanResultType: ScanResultType.BREACH_LENSE,
    };

    this.demoPentestScans = [...this.demoPentestScans, darknetScan];
    return ids ? this.demoPentestScans.filter((s) => ids.includes(s.id)) : this.demoPentestScans;
  }

  private generateFindings(
    def: {
      templateIdx?: number;
      weekOffset?: number;
      counts: Partial<Record<Severity, number>>;
    },
    pentestType: PentestType,
  ) {
    const findings: FindingDto[] = [];
    Object.entries(def.counts).forEach(([severityStr, count]) => {
      const severity = parseInt(severityStr) as Severity;
      const templates = this.FINDING_TEMPLATES[severity];
      if (!templates) return;
      for (let j = 0; j < (count ?? 0); j++) {
        const t = templates[j % templates.length];
        let score: number = 0;
        if (severity === Severity.Critical) score = this.getRandomScore(9.0, 10.1);
        if (severity === Severity.High) score = this.getRandomScore(7.0, 9.0);
        if (severity === Severity.Medium) score = this.getRandomScore(4.0, 7.0);
        if (severity === Severity.Low) score = this.getRandomScore(0.1, 4.0);
        if (severity === Severity.Info) score = this.getRandomScore(0.0, 0.1);

        findings.push({
          name: t.name,
          description: t.description,
          severity,
          score,
          scanModule: ScanModule.Horizon,
          scanType: pentestType === PentestType.ExternalAttack ? ScanType.ExternalPentest : ScanType.InternalPentest,
        });
      }
    });
    return findings;
  }

  private getRandomScore(min: number, max: number) {
    const minCeiled = Math.ceil(min * 10);
    const maxFloored = Math.floor(max * 10);
    return Math.floor(Math.random() * (maxFloored - minCeiled) + minCeiled) / 10;
  }

  getBoxes(): Box[] {
    if (this.demoBoxes) {
      return this.demoBoxes;
    }
    const demoBoxConfigs = [
      {
        displayName: 'Demo DEFENDERBOX 1',
        name: 'dbx-demo-01',
        os: OperatingSystem.Hardware,
        status: Severity.Critical,
        connected: true,
      },
      {
        displayName: 'Demo DEFENDERBOX 2',
        name: 'dbx-demo-02',
        os: OperatingSystem.Hardware,
        status: Severity.High,
        connected: true,
      },
      {
        displayName: 'Demo virtuel DEFENDERBOX 1',
        name: 'dbx-vdemo-01',
        os: OperatingSystem.VirtualBox,
        status: Severity.Low,
        connected: true,
      },
      {
        displayName: 'Demo virtuel DEFENDERBOX 2',
        name: 'dbx-vdemo-02',
        os: OperatingSystem.HyperV,
        status: Severity.Low,
        connected: true,
      },
      {
        displayName: 'Demo virtuel DEFENDERBOX 3',
        name: 'dbx-vdemo-03',
        os: OperatingSystem.VMware,
        status: Severity.Low,
        connected: false,
      },
    ];

    this.demoBoxes = demoBoxConfigs.map((config, i) => ({
      id: `demo-box-${i + 1}`,
      displayName: config.displayName,
      name: config.name,
      os: config.os,
      customer: DEMO_CUSTOMER,
      manager: DEMO_PARTNER,
      runner: {
        id: `demo-runner-${i + 1}`,
        displayName: `Demo Runner ${i + 1}`,
        name: `runner-demo-0${i + 1}`,
      },
      connected: config.connected,
      ipv4: [`192.168.1.${50 + i}`],
      version: '1.2.3',
      timezone: 'Europe/Berlin',
      status: config.status,
    }));
    return this.demoBoxes;
  }

  updateBox(id: string, boxData: AdminBoxUpdateRequestDto | BoxUpdateRequest): Box {
    const boxes = this.getBoxes();
    const index = boxes.findIndex((b) => b.id === id);
    if (index === -1) {
      throw new Error(`Demo box with id "${id}" not found`);
    }

    const isDto = 'partnerId' in boxData || 'customerId' in boxData;
    let customer = boxes[index].customer;
    let manager = boxes[index].manager;
    if (isDto) {
      const dto = boxData as AdminBoxUpdateRequestDto;
      if (dto.customerId === null) {
        customer = undefined;
      } else if (dto.customerId !== undefined) {
        const found = this.getCustomers().find((c: CustomerLightData) => c.id === dto.customerId);
        customer = found ? (found as any) : customer;
      }
      if (dto.partnerId === null) {
        manager = undefined;
      } else if (dto.partnerId !== undefined) {
        const found = this.getPartners().find((p) => p.id === dto.partnerId);
        manager = found ? (found as any) : manager;
      }
    } else {
      const legacy = boxData as BoxUpdateRequest;
      customer = legacy.customer !== undefined ? legacy.customer : customer;
      manager = legacy.manager !== undefined ? legacy.manager : manager;
    }

    const updatedBox = {
      ...boxes[index],
      displayName:
        boxData.displayName !== undefined
          ? (boxData.displayName ?? boxes[index].displayName)
          : boxes[index].displayName,
      customer,
      manager,
    };
    this.demoBoxes = boxes.map((box, i) => (i === index ? updatedBox : box));
    return updatedBox;
  }

  deleteBox(id: string): void {
    const boxes = this.getBoxes();
    this.demoBoxes = boxes.filter((b) => b.id !== id);
  }

  getUsers(): User[] {
    if (this.demoUsers) {
      return this.demoUsers;
    }
    this.demoUsers = Array.from({ length: 8 }, (_, i) => ({
      id: -(i + 1),
      firstName: 'Demo',
      lastName: `User ${i + 1}`,
      email: `demo${i + 1}@${environment.rootUrl}`,
      customer: DEMO_CUSTOMER,
      partner: DEMO_PARTNER,
      active: i !== 7, // 7 active, 1 inactive
      role: i === 0 ? Role.GlobalAdmin : i === 1 ? Role.Partner : Role.User,
      note: null,
    }));
    return this.demoUsers;
  }

  getIdentecoConfigs(): IdentecoConfigBase[] {
    if (this.demoIdentecoConfigs) {
      return this.demoIdentecoConfigs;
    }
    this.demoIdentecoConfigs = Array.from({ length: 2 }, (_, i) => ({
      id: -(i + 1),
      domain: `domain${i + 1}.de`,
      isVerified: i === 0,
    }));
    return this.demoIdentecoConfigs;
  }

  getBreachLenseConfigs(): BreachLenseConfigBase[] {
    if (this.demoBreachLenseConfigs) {
      return this.demoBreachLenseConfigs;
    }

    this.demoBreachLenseConfigs = [
      {
        id: -1,
        domain: 'example.com',
      },
    ];

    return this.demoBreachLenseConfigs;
  }

  getDarknetScanEmailAddresses(): DarknetScanEmailAddress[] {
    if (this.demoDarknetScanEmailAddresses) {
      return this.demoDarknetScanEmailAddresses;
    }

    this.demoDarknetScanEmailAddresses = [
      {
        id: 'email-id-1',
        emailAddress: 'demo1@example.com',
      },
      {
        id: 'email-id-2',
        emailAddress: 'demo2@example.com',
      },
      {
        id: 'email-id-3',
        emailAddress: 'demo3@example.com',
      },
    ];

    return this.demoDarknetScanEmailAddresses;
  }

  addDarknetScanEmailAddresses(emailAddresses: string[]): DarknetScanEmailAddress[] {
    if (this.demoDarknetScanEmailAddresses) {
      const emailsToAdd = emailAddresses.map((emailAddress, index) => ({
        id: `email-id-${index}`,
        emailAddress,
      }));

      this.demoDarknetScanEmailAddresses = [...this.demoDarknetScanEmailAddresses, ...emailsToAdd];
      return emailsToAdd;
    }

    return this.demoDarknetScanEmailAddresses ?? [];
  }

  deleteDarknetScanEmailAddress(emailAddress: DarknetScanEmailAddress): void {
    if (this.demoDarknetScanEmailAddresses) {
      this.demoDarknetScanEmailAddresses = this.demoDarknetScanEmailAddresses.filter(
        (actual) => emailAddress.id != actual.id,
      );
    }
  }

  getPartnerStatistics(): PartnerStatistics {
    return {
      customersStatistics: {
        total: 25,
        active: 20,
        newAdded: 3,
      },
      boxesStatistics: {
        total: 30,
        active: 28,
        newAdded: 5,
        sold: 25,
        outdated: 1,
      },
    };
  }

  getPartnerTopCustomers(): any[] {
    return [
      { name: 'Customer A', boxes: 10 },
      { name: 'Customer B', boxes: 8 },
      { name: 'Customer C', boxes: 5 },
      { name: 'Customer D', boxes: 4 },
      { name: 'Customer E', boxes: 3 },
    ];
  }

  getPartnerTopBoxes(): any[] {
    return [
      { name: 'Box 01', weaknesses: 15 },
      { name: 'Box 02', weaknesses: 12 },
      { name: 'Box 03', weaknesses: 10 },
      { name: 'Box 04', weaknesses: 8 },
      { name: 'Box 05', weaknesses: 5 },
    ];
  }

  getPartnerCustomersChart(): any {
    return {
      labels: ['January', 'February', 'March', 'April', 'May', 'June'],
      values: [10, 12, 15, 18, 22, 25],
      boxesValues: [15, 18, 22, 25, 28, 30],
    };
  }

  getPartnerBoxesChart(): any {
    return {
      labels: ['January', 'February', 'March', 'April', 'May', 'June'],
      values: [15, 18, 22, 25, 28, 30],
    };
  }

  getPentestScanStatistics(): any[] {
    return this.getPentestScans().map((scan) => {
      const findings = this.getPentestScanFindings(scan.id);
      const getStats = (items: FindingDto[], scale: number = 1) => ({
        critical: Math.round(items.filter((f) => f.severity === Severity.Critical).length * scale),
        high: Math.round(items.filter((f) => f.severity === Severity.High).length * scale),
        medium: Math.round(items.filter((f) => f.severity === Severity.Medium).length * scale),
        low: Math.round(items.filter((f) => f.severity === Severity.Low).length * scale),
      });

      // impacts scale is dynamically calculated for this scan to match scan.impactsCount
      const impactsScale = findings.length > 0 ? scan.impactsCount / findings.length : 0.1;

      return {
        pentestScan: {
          id: scan.id,
          name: scan.name,
          createdAt: scan.createdAt,
          completedAt: scan.completedAt,
        },
        statistics: {
          impacts: getStats(findings, impactsScale),
          weaknesses: getStats(findings),
          credentials: getStats(findings.filter((f) => f.name.toLowerCase().includes('credential'))),
        },
      };
    });
  }

  getBreachLenseFindings(): BreachLenseFindingDto[] {
    if (this.demoBreachLenseFindings) {
      return this.demoBreachLenseFindings;
    }

    this.demoBreachLenseFindings = [
      {
        name: 'demo1@example.com',
        description: 'Demo email found in a breached data source.',
        severity: Severity.Critical,
        score: 10.0,
        scanModule: ScanModule.BreachLense,
        scanType: ScanType.ExternalPentest,
        emailAddress: 'demo1@example.com',
        passwordShortHash: 'a1b2c3d4e5f60718',
        maskedPassword: 'D********1',
        isFixed: false,
        isNewLeak: true,
        numberOfLeaks: 15,
        affiliation: 'LinkedIn',
      },
      {
        name: 'demo2@example.com',
        description: 'Demo email found in a breached data source.',
        severity: Severity.Critical,
        score: 10.0,
        scanModule: ScanModule.BreachLense,
        scanType: ScanType.ExternalPentest,
        emailAddress: 'demo2@example.com',
        passwordShortHash: 'b2c3d4e5f6071829',
        maskedPassword: 'D********2',
        isFixed: false,
        isNewLeak: true,
        numberOfLeaks: 4,
        affiliation: 'Facebook',
      },
      {
        name: 'demo3@example.com',
        description: 'Demo email found in a breached data source.',
        severity: Severity.Critical,
        score: 10.0,
        scanModule: ScanModule.BreachLense,
        scanType: ScanType.ExternalPentest,
        emailAddress: 'demo3@example.com',
        passwordShortHash: 'c3d4e5f607182930',
        maskedPassword: 'D********3',
        isFixed: false,
        isNewLeak: true,
        numberOfLeaks: 2,
        affiliation: 'Twitter',
      },
      {
        name: 'demo1@example.com',
        description: 'Demo email found in a breached data source.',
        severity: Severity.Critical,
        score: 10.0,
        scanModule: ScanModule.BreachLense,
        scanType: ScanType.ExternalPentest,
        emailAddress: 'demo1@example.com',
        passwordShortHash: 'a1b2c3d4e5f60718',
        maskedPassword: 'D********1',
        isFixed: false,
        isNewLeak: true,
        numberOfLeaks: 2,
        affiliation: 'Dropbox',
      },
      {
        name: 'demo2@example.com',
        description: 'Demo email found in a breached data source.',
        severity: Severity.Critical,
        score: 10.0,
        scanModule: ScanModule.BreachLense,
        scanType: ScanType.ExternalPentest,
        emailAddress: 'demo2@example.com',
        passwordShortHash: 'b2c3d4e5f6071829',
        maskedPassword: 'D********2',
        isFixed: false,
        isNewLeak: true,
        numberOfLeaks: 2,
        affiliation: 'LinkedIn',
      },
      {
        name: 'demo3@example.com',
        description: 'Demo email found in a breached data source.',
        severity: Severity.Critical,
        score: 10.0,
        scanModule: ScanModule.BreachLense,
        scanType: ScanType.ExternalPentest,
        emailAddress: 'demo3@example.com',
        passwordShortHash: 'c3d4e5f607182930',
        maskedPassword: 'D********3',
        isFixed: false,
        isNewLeak: true,
        numberOfLeaks: 2,
        affiliation: 'Facebook',
      },
      {
        name: 'demo1@example.com',
        description: 'Demo email found in a breached data source.',
        severity: Severity.Critical,
        score: 10.0,
        scanModule: ScanModule.BreachLense,
        scanType: ScanType.ExternalPentest,
        emailAddress: 'demo1@example.com',
        passwordShortHash: 'a1b2c3d4e5f60718',
        maskedPassword: 'D********1',
        isFixed: false,
        isNewLeak: true,
        numberOfLeaks: 2,
        affiliation: 'Twitter',
      },
      {
        name: 'demo2@example.com',
        description: 'Demo email found in a breached data source.',
        severity: Severity.Critical,
        score: 10.0,
        scanModule: ScanModule.BreachLense,
        scanType: ScanType.ExternalPentest,
        emailAddress: 'demo2@example.com',
        passwordShortHash: 'b2c3d4e5f6071829',
        maskedPassword: 'D********2',
        isFixed: false,
        isNewLeak: true,
        numberOfLeaks: 2,
        affiliation: 'Dropbox',
      },
    ];

    return this.demoBreachLenseFindings;
  }

  getPentestScanFindings(scanId: string): FindingDto[] {
    this.getPentestScans(); // Ensure data is generated
    if (scanId === 'demo-darknet-scan-example-com') {
      return this.getBreachLenseFindings();
    }
    if (this.demoPentestFindings[scanId]) {
      return this.demoPentestFindings[scanId];
    }

    if (scanId.startsWith('demo-partner-scan-')) {
      const parts = scanId.split('-');
      const customerId = parseInt(parts[3], 10);
      const week = parseInt(parts[4], 10);

      const customer = this.getDemoCustomers().find((c) => c.id === customerId);
      if (customer) {
        const customerIdx = Math.abs(customerId) - 1;
        const stats = (this.customerWeeklyStats[customerIdx] || this.customerWeeklyStats[9]).find(
          (s) => s.week === week,
        );
        if (stats) {
          const counts: Partial<Record<Severity, number>> = {
            [Severity.Critical]: stats.critical,
            [Severity.High]: stats.high,
            [Severity.Medium]: stats.medium,
            [Severity.Low]: stats.low,
          };
          const findings = this.generateFindings({ counts }, PentestType.NodeZero);
          this.demoPentestFindings[scanId] = findings;
          return findings;
        }
      }
    }

    return [];
  }

  getPartnerPentestScanStatistics(): any[] {
    const customers = this.getDemoCustomers();
    const today = new Date();

    return customers.map((customer, index) => {
      const stats = this.customerWeeklyStats[index] || this.customerWeeklyStats[9];
      const scansPerCustomer: any[] = [];

      stats.forEach((weekStat) => {
        const generated = this.generateCustomerScans(customer, today, weekStat);
        scansPerCustomer.push(...generated);
      });

      return {
        customer: {
          id: customer.id,
          companyName: customer.companyName,
        },
        statistics: scansPerCustomer,
      };
    });
  }

  private generateCustomerScans(
    customer: CustomerLightData,
    today: Date,
    weekStat: {
      week: number;
      scans: number;
      critical: number;
      high: number;
      medium: number;
      low: number;
    },
  ): any[] {
    const scans: any[] = [];
    const scanTemplates = [
      { name: 'LAN', type: PentestType.NodeZero },
      { name: 'External', type: PentestType.ExternalAttack },
    ];

    for (let i = 0; i < weekStat.scans; i++) {
      const template = scanTemplates[i % scanTemplates.length];
      const scanId = `demo-partner-scan-${customer.id}-${weekStat.week}-${i}`;
      const completedAt = subDays(today, weekStat.week * 7 + i * 2 + (customer.id % 3));

      scans.push({
        pentestScan: {
          id: scanId,
          name: `${template.name} - ${customer.companyName}`,
          completedAt: completedAt,
        },
        statistics: {
          impacts: {
            critical: Math.round(weekStat.critical / weekStat.scans),
            high: Math.round(weekStat.high / weekStat.scans),
            medium: Math.round(weekStat.medium / weekStat.scans),
            low: Math.round(weekStat.low / weekStat.scans),
          },
          weaknesses: {
            critical: Math.round((weekStat.critical * 2) / weekStat.scans),
            high: Math.round((weekStat.high * 2) / weekStat.scans),
            medium: Math.round((weekStat.medium * 2) / weekStat.scans),
            low: Math.round((weekStat.low * 2) / weekStat.scans),
          },
          credentials: {
            critical: Math.round(weekStat.critical / 2 / weekStat.scans),
            high: Math.round(weekStat.high / 2 / weekStat.scans),
            medium: Math.round(weekStat.medium / 2 / weekStat.scans),
            low: Math.round(weekStat.low / 2 / weekStat.scans),
          },
        },
      });
    }
    return scans;
  }

  getApplicationEvents(): ApplicationEvent[] {
    const today = new Date();
    return [
      {
        performedBy: 'Demo User',
        entity: 'New Pentest Config',
        type: ApplicationEventType.PentestConfigCreated,
        occurredAt: subDays(today, 1),
      },
      {
        performedBy: 'Demo Admin',
        entity: 'Demo Customer',
        type: ApplicationEventType.CustomerCreated,
        occurredAt: subDays(today, 30),
      },
    ];
  }

  getScanConfigurations(): ScanConfig[] {
    if (this.demoScanConfigurations) {
      return this.demoScanConfigurations;
    }
    const today = new Date();
    this.demoScanConfigurations = [
      {
        id: 'demo-config-lan',
        name: 'LAN',
        runnerId: this.getBoxes()[1 % this.getBoxes().length].runner!!.id,
        lastResultStatus: LastResultStatus.RUNNING,
        lastResultInfo: 'Last scan completed successfully',
        recipients: [{ recipient: 'Admin', emailAddress: 'admin@' + environment.rootUrl }],
        runWeekly: true,
        nextRunTime: subHours(subDays(today, -2), Math.floor(Math.random() * 12) * 2),
        internalScansEnabled: true,
        externalScansEnabled: false,
        websiteScansEnabled: false,
        darknetScansEnabled: false,
        scanIntervalInWeeks: 1,
        availableActions: ['pause', 'stop'],
      },
      {
        id: 'demo-config-dmz',
        name: 'DMZ',
        runnerId: this.getBoxes()[2 % this.getBoxes().length].runner!!.id,
        lastResultStatus: LastResultStatus.SUCCESS,
        lastResultInfo: 'Last scan completed successfully',
        recipients: [{ recipient: 'Admin', emailAddress: 'admin@' + environment.rootUrl }],
        runWeekly: true,
        nextRunTime: subHours(subDays(today, -3), Math.floor(Math.random() * 12) * 2),
        internalScansEnabled: true,
        externalScansEnabled: false,
        websiteScansEnabled: false,
        darknetScansEnabled: false,
        scanIntervalInWeeks: 1,
        availableActions: ['start'],
      },
      {
        id: 'demo-config-extern',
        name: 'extern',
        runnerId: this.getBoxes()[3 % this.getBoxes().length].runner!!.id,
        lastResultStatus: LastResultStatus.RUNNING,
        lastResultInfo: 'Scan is currently running',
        recipients: [{ recipient: 'Admin', emailAddress: 'admin@' + environment.rootUrl }],
        runWeekly: true,
        nextRunTime: subHours(subDays(today, -4), Math.floor(Math.random() * 12) * 2),
        internalScansEnabled: false,
        externalScansEnabled: true,
        websiteScansEnabled: true,
        darknetScansEnabled: true,
        scanIntervalInWeeks: 1,
        availableActions: ['pause', 'stop'],
      },
      {
        id: 'demo-config-lab',
        name: 'Lab',
        runnerId: this.getBoxes()[4 % this.getBoxes().length].runner!!.id,
        lastResultStatus: LastResultStatus.SUCCESS,
        lastResultInfo: 'Last scan failed: check connectivity',
        recipients: [{ recipient: 'Admin', emailAddress: 'admin@' + environment.rootUrl }],
        runWeekly: true,
        nextRunTime: subHours(subDays(today, -1), Math.floor(Math.random() * 12) * 2),
        internalScansEnabled: true,
        externalScansEnabled: false,
        websiteScansEnabled: false,
        darknetScansEnabled: false,
        scanIntervalInWeeks: 1,
        availableActions: ['start'],
      },
    ];
    return this.demoScanConfigurations;
  }

  getPartnerPentestCalendarEvents(): PentestCalendarEvent[] {
    if (this.demoPartnerPentestCalendarEvents) {
      return this.demoPartnerPentestCalendarEvents;
    }
    const configs = this.getScanConfigurations();
    const customers = this.getDemoCustomers();
    this.demoPartnerPentestCalendarEvents = [];

    customers.forEach((customer, customerIdx) => {
      configs.forEach((config, configIdx) => {
        for (let weekOffset = -13; weekOffset <= 13; weekOffset++) {
          const startDate = subHours(subDays(config.nextRunTime, weekOffset * 7), customerIdx ** 3);

          const types: ('internal' | 'external' | 'web')[] = [];
          if (config.internalScansEnabled) types.push('internal');
          if (config.externalScansEnabled) types.push('external');
          if (config.websiteScansEnabled) types.push('web');

          this.demoPartnerPentestCalendarEvents?.push({
            id: customerIdx + configIdx * 100 + (weekOffset + 13),
            customerName: customer.companyName,
            name: config.name,
            types: types,
            startDate: startDate,
            endDate: addMinutes(new Date(startDate.getTime() + 4 * 3600 * 1000), this.getRandomScore(0, 120)),
            configId: 234,
            customerId: 423,
          });
        }
      });
    });

    return this.demoPartnerPentestCalendarEvents;
  }

  getPentestCalendarEvents(): PentestCalendarEvent[] {
    if (this.demoPentestCalendarEvents) {
      return this.demoPentestCalendarEvents;
    }
    const configs = this.getScanConfigurations();
    this.demoPentestCalendarEvents = [];

    configs.forEach((config, configIdx) => {
      for (let weekOffset = -13; weekOffset <= 13; weekOffset++) {
        const startDate = subDays(config.nextRunTime, weekOffset * 7);

        const types: ('internal' | 'external' | 'web')[] = [];
        if (config.internalScansEnabled) types.push('internal');
        if (config.externalScansEnabled) types.push('external');
        if (config.websiteScansEnabled) types.push('web');

        this.demoPentestCalendarEvents?.push({
          id: configIdx * 100 + (weekOffset + 13),
          customerName: 'Demo Customer',
          name: config.name,
          types: types,
          startDate: startDate,
          endDate: new Date(startDate.getTime() + 4 * 3600 * 1000),
          configId: 234,
          customerId: 423,
        });
      }
    });

    return this.demoPentestCalendarEvents;
  }

  getGetPentestConfigData(configId?: string): GetPentestConfigData {
    const configs = this.getScanConfigurations();
    const config = configs.find((c) => c.id === configId) || configs[0];

    return {
      id: -1,
      name: config.name,
      internalScansEnabled: config.internalScansEnabled,
      externalScansEnabled: config.externalScansEnabled,
      websiteScansEnabled: config.websiteScansEnabled,
      darknetScansEnabled: config.darknetScansEnabled,
      nextRunTime: config.nextRunTime,
      interval: config.scanIntervalInWeeks,
      nessusId: 'nessus-1',
      horizonRunnerId: config.runnerId,
      horizonInternalTemplateId: 'tmpl-1',
      horizonAssetId: 'asset-1',
      recipients: config.recipients.map((r) => r.emailAddress),
    };
  }

  getMailConfig(): MailConfig {
    return {
      configId: -1,
      configName: 'Demo Mail Config',
      language: LanguageEnum.English,
      reportType: ReportTypeEnum.detailed,
      recipients: ['demo-reports@' + environment.rootUrl],
    };
  }

  getCustomers(): CustomerLightData[] {
    return [DEMO_CUSTOMER];
  }

  getDemoCustomers(): Customer[] {
    return Array.from({ length: 10 }, (_, i) => ({
      ...DEMO_CUSTOMER,
      id: (i + 1) * -1,
      companyName: `Demo Customer ${i + 1}`,
    }));
  }

  getPartners(): Partner[] {
    return [DEMO_PARTNER];
  }

  getAllPartners(): PartnerLightData[] {
    return [DEMO_PARTNER_LIGHT];
  }

  getDemoUserAccount(): UserAccount {
    return {
      id: -1,
      firstName: 'Demo',
      lastName: 'User',
      email: 'demo@' + environment.rootUrl,
      customer: DEMO_CUSTOMER,
      partner: DEMO_PARTNER,
      role: Role.User,
      isAdmin: false,
      isUser: true,
      isPartner: false,
      language: LanguageEnum.English,
    };
  }
}
